import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import React, { FC, useState } from 'react';

interface RatingProps {
  highText?: string;
  lowText?: string;
  min: number;
  max: number;
  font?: string;
  onRatingClick?: (rating: number) => void;
  isSmallScreen: boolean;
  currentRating: number | undefined;
}

const Rating: FC<RatingProps> = ({
  min,
  max,
  font,
  onRatingClick,
  highText,
  lowText,
  isSmallScreen,
  currentRating,
}) => {
  const [clickedRating, setClickedRating] = useState<number | undefined>(currentRating);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  const handleCircleClick = (rating: number) => {
    setClickedRating(rating);
    if (onRatingClick) {
      onRatingClick(rating);
    }
  };

  const getCircleColor = (rating: number) => {
    if (rating >= 0 && rating <= 6) {
      return 'linear-gradient(45deg, #ff5f5f 90%, #ff8080 90%)';
    } else if (rating === 7 || rating === 8) {
      return 'linear-gradient(45deg, #ffa860 90%, #ffc080 90%)';
    } else if (rating === 9 || rating === 10) {
      return 'linear-gradient(45deg, #80e0b0 90%, #99e6c0 90%)';
    }
  };

  const renderCircles = (
    max: number,
    min: number,
    getCircleColor: (rating: number) => any,
    isSmallScreen: boolean,
    setHoveredRating: (rating: number | null) => void,
    handleCircleClick: (rating: number) => void,
    clickedRating: number | undefined,
    hoveredRating: number | null,
    font?: string
  ) => {
    return Array.from({ length: max - min + 1 }, (_, index) => {
      const rating = min + index;
      const circleColor = getCircleColor(rating);
      return !isSmallScreen ? (
        <Box
          key={rating}
          className="rating-circle survey-rating-circle"
          style={{
            background: circleColor,
            height: 50,
            width: 50,
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontSize: 25,
            fontFamily: font ? `${font}, Arial, sans-serif` : undefined,
            boxShadow: '0px 3px 15px rgba(0, 0, 0, 0.2)',
            margin: '8px',
            transition: 'all 0.4s ease-in-out',
          }}
          onMouseEnter={() => setHoveredRating(rating)}
          onMouseLeave={() => setHoveredRating(null)}
          onClick={() => handleCircleClick(rating)}
          sx={{
            transform:
              rating === clickedRating
                ? 'scale(1.2) translateY(-10px)'
                : rating === hoveredRating
                ? 'scale(1.1)'
                : '',
            cursor: 'pointer',
          }}
        >
          {rating}
        </Box>
      ) : (
        <FormControl
          className="survey-rating-control"
          key={rating}
          sx={{ marginRight: -3 }}
        >
          <RadioGroup
            className="survey-rating-radio-group"
            row
            aria-labelledby="demo-form-control-label-placement"
            name="rating"
            value={Number.isFinite(clickedRating) ? String(clickedRating) : ''}
            onChange={(e) => handleCircleClick(Number(e.target.value))}
          >
            <FormControlLabel
              className="survey-rating-radio"
              value={rating.toString()}
              control={<Radio sx={{ padding: '6px' }} />}
              label={rating.toString()}
              labelPlacement="top"
              sx={{ fontSize: '1.5rem', marginLeft: 0, fontFamily: font }}
            />
          </RadioGroup>
        </FormControl>
      );
    });
  };

  return (
    <>
      <Box
        className="rating survey-rating"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-evenly',
          margin: '0 auto',
        }}
      >
        {renderCircles(
          max,
          min,
          getCircleColor,
          isSmallScreen,
          setHoveredRating,
          handleCircleClick,
          clickedRating,
          hoveredRating,
          font
        )}
        <Box
          className="survey-rating-labels"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            fontSize: { xs: 12, sm: 14 },
            fontFamily: font,
            color: '#333',
            padding: '20px 0',
            margin: { xs: 0, sm: '0 10px' },
          }}
        >
          <Typography
            className="survey-rating-label survey-rating-label--low"
            sx={{ textAlign: 'left' }}
          >
            {lowText}
          </Typography>
          <Typography
            className="survey-rating-label survey-rating-label--high"
            sx={{ textAlign: 'right' }}
          >
            {highText}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default Rating;
